/**
 * Константы темы приложения
 * Используются для унификации стилей и цветов
 */

// Цветовая палитра (синхронизирована с Ant Design)
export const COLORS = {
  primary: '#1890ff',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  info: '#1890ff',
  
  // Оттенки серого
  gray: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e8e8e8',
    300: '#d9d9d9',
    400: '#bfbfbf',
    500: '#8c8c8c',
    600: '#595959',
    700: '#434343',
    800: '#262626',
    900: '#1f1f1f',
  },
} as const;

// Типографика
export const TYPOGRAPHY = {
  fontFamily: {
    base: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
  },
  fontSize: {
    xs: 12,
    sm: 13,
    base: 18,
    lg: 16,
    xl: 18,
    '2xl': 20,
    '3xl': 24,
    '4xl': 30,
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    base: 1.5,
    relaxed: 1.75,
  },
} as const;

// Отступы и размеры (✅ КОМПАКТНЫЕ значения)
export const SPACING = {
  xs: 3,   // Было 4px
  sm: 6,   // Было 8px
  md: 12,  // Было 16px
  lg: 16,  // Было 24px
  xl: 24,  // Было 32px
  '2xl': 36, // Было 48px
  '3xl': 48, // Было 64px
} as const;

// Радиусы скругления
export const BORDER_RADIUS = {
  none: 0,
  sm: 2,
  base: 6,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
} as const;

// Тени
export const SHADOWS = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
} as const;

// Z-index слои
export const Z_INDEX = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
} as const;

// Анимации
export const ANIMATIONS = {
  duration: {
    fast: 150,
    base: 300,
    slow: 500,
  },
  easing: {
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

// Breakpoints (синхронизировано с Tailwind)
export const BREAKPOINTS = {
  xs: 480,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  '2xl': 1600,
} as const;

// Layout размеры (✅ КОМПАКТНЫЕ значения)
export const LAYOUT = {
  headerHeight: 52,           // Было 64px - уменьшен header
  sidebarWidth: 240,          // Было 256px
  sidebarCollapsedWidth: 64,  // Было 80px
  contentMaxWidth: 1600,
  footerHeight: 48,           // Было 64px - уменьшен footer
} as const;

// Статусы клиентов (цвета)
export const CUSTOMER_STATUS_COLORS = {
  1: '#52c41a', // Активен
  2: '#faad14', // Приостановлен
  3: '#ff4d4f', // Отключен
  4: '#1890ff', // Новый
  5: '#8c8c8c', // Архив
} as const;

// Типы устройств (иконки)
export const DEVICE_TYPES = {
  switch: 'Switch',
  router: 'Router',
  olt: 'OLT',
  onu: 'ONU',
  radio: 'Radio',
  cwdm: 'CWDM',
  medical: 'Medical',
} as const;

// Цвета слоёв карты
export const MAP_LAYER_COLORS = {
  customer: '#10b981',  // Зелёный для клиентов
  house: '#ef4444',     // Красный для домов (по умолчанию)
  node: '#3b82f6',      // Синий для узлов
  cable: '#8b5cf6',     // Фиолетовый для кабельных линий
  device: '#f97316',    // Оранжевый для устройств
  label: '#7c3aed',     // Фиолетовый для меток
  image: '#0ea5e9',     // Голубой для изображений
  polygon: '#f59e0b',  // Оранжевый для полигонов
  cluster: '#808080',   // Серый для кластеров
  selected: '#ff4d4f',  // Красный для выбранных объектов
  default: '#2563eb',   // Синий по умолчанию
} as const;

// Цвета для стилей домов (houseGreen, houseYellow, houseRed, houseBlack)
export const MAP_HOUSE_STYLE_COLORS = {
  houseGreen: '#10b981',  // Зелёный - активное здание
  houseYellow: '#fbbf24',  // Жёлтый - неактивное здание
  houseRed: '#ef4444',     // Красный - новое здание без активности
  houseBlack: '#1f2937',   // Чёрный - неиспользуемое здание
} as const;

// Цвета для редактирования геометрии
export const MAP_GEOMETRY_COLORS = {
  polyline: '#1677ff',  // Синий для линий
  polygon: '#f97316',   // Оранжевый для полигонов
  point: '#1677ff',     // Синий для точек
  buffer: '#ff4d4f',    // Красный для буфера
  route: '#52c41a',     // Зелёный для маршрута
} as const;

// Размеры символов на карте
export const MAP_SYMBOL_SIZES = {
  point: 8,
  line: 4,
  polygon: 2,
  cluster: 16,
} as const;

export default {
  COLORS,
  TYPOGRAPHY,
  SPACING,
  BORDER_RADIUS,
  SHADOWS,
  Z_INDEX,
  ANIMATIONS,
  BREAKPOINTS,
  LAYOUT,
  CUSTOMER_STATUS_COLORS,
  DEVICE_TYPES,
  MAP_LAYER_COLORS,
  MAP_GEOMETRY_COLORS,
  MAP_SYMBOL_SIZES,
};

